import { GuardianBroadcastChannel } from '@/types/composition-functions';
import type { BroadcastToGuardiansMutation, ExportStudentLinkingCodesQuery, GuardianFilterOptionsQuery } from '@/types/operations';

/** Admin-side guardian helpers: invites, broadcast, linking codes, filters. */

/** Guardians an invite can go to: they have an email and no portal account yet. */
export function invitableGuardians<G extends { email?: string | null; user?: unknown }>(guardians: ReadonlyArray<G>): G[] {
  return guardians.filter(g => !!g.email && !g.user);
}

export function broadcastChannels(selection: { email: boolean; whatsapp: boolean }): GuardianBroadcastChannel[] {
  return [
    ...(selection.email ? [GuardianBroadcastChannel.Email] : []),
    ...(selection.whatsapp ? [GuardianBroadcastChannel.Whatsapp] : []),
  ];
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "3 emails, 2 WhatsApp, 1 skipped". */
export function broadcastResultSummary(result: BroadcastToGuardiansMutation['broadcastToGuardians'] | null | undefined): string {
  if (!result) return 'Messages queued for delivery.';
  const parts = [
    result.emailsSent > 0 ? plural(result.emailsSent, 'email') : '',
    result.whatsappSent > 0 ? `${result.whatsappSent} WhatsApp` : '',
    result.skipped > 0 ? `${result.skipped} skipped` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Messages queued for delivery.';
}

type LinkingCodeRow = ExportStudentLinkingCodesQuery['exportStudentLinkingCodes'][number];

const csvCell = (value: string | null | undefined) => `"${(value ?? '').replace(/"/g, '""')}"`;

/** The linking codes export as CSV text (quoted, header row first). */
export function linkingCodesCsv(rows: ReadonlyArray<LinkingCodeRow>): string {
  const header = 'Student Name,Matric/Admission No,Class,Programme,Linking Code';
  const lines = rows.map(r => [r.studentName, r.matricNo, r.className, r.programmeName, r.linkingCode].map(csvCell).join(','));
  return [header, ...lines].join('\n');
}

type Faculty = GuardianFilterOptionsQuery['listSchoolFaculties']['data'][number];
type Department = NonNullable<NonNullable<Faculty['departments']>[number]>;
type Programme = NonNullable<NonNullable<Department['programmes']>[number]>;

const present = <T>(items: ReadonlyArray<T | null | undefined> | null | undefined): T[] =>
  (items ?? []).filter((item): item is T => !!item);

/** Departments of the chosen faculty, or of every faculty. */
export function departmentsFor(faculties: ReadonlyArray<Faculty>, facultyId: string | null | undefined): Department[] {
  const source = facultyId ? faculties.filter(f => String(f.id) === String(facultyId)) : faculties;
  return source.flatMap(f => present(f.departments));
}

/** Programmes of the chosen department, or of every listed department. */
export function programmesFor(departments: ReadonlyArray<Department>, departmentId: string | null | undefined): Programme[] {
  const source = departmentId ? departments.filter(d => String(d.id) === String(departmentId)) : departments;
  return source.flatMap(d => present(d.programmes));
}

export { present as presentItems };
